/**
 * FlowControls component - Preview and annotate buttons for sidebar flow
 * Factory function following the app's architectural patterns
 */

import {
  createIconButton,
  type IconButtonAPI,
  type IconButtonConfig,
} from "@UI/IconButton/index.js";

export interface FlowControlsConfig {
  readonly onPreviewClick?: () => void;
  readonly onAnnotateClick?: () => void;
}

export interface FlowControlsAPI {
  readonly getElement: () => HTMLElement;
  readonly setPreviewDisabled: (disabled: boolean) => void;
  readonly setAnnotateDisabled: (disabled: boolean) => void;
  readonly setPreviewVisible: (visible: boolean) => void;
  readonly setAnnotateVisible: (visible: boolean) => void;
  readonly setPreviewIcon: (icon: string) => void;
  readonly setAnnotateIcon: (icon: string) => void;
  readonly destroy: () => void;
}

/**
 * Creates FlowControls component with preview and annotate buttons
 * @param config - Configuration for the flow controls
 * @returns FlowControls API
 */
export const createFlowControls = (
  config: FlowControlsConfig = {}
): FlowControlsAPI => {
  const { onPreviewClick, onAnnotateClick } = config;

  let isDestroyed = false;
  let previewButtonAPI: IconButtonAPI | null = null;
  let annotateButtonAPI: IconButtonAPI | null = null;
  let previewVisible = true;
  let annotateVisible = true;

  // Create container element
  const container = document.createElement("div");
  container.className = "flow-controls";

  const updateContainerVisibility = (): void => {
    container.style.display =
      previewVisible || annotateVisible ? "flex" : "none";
  };

  /**
   * Initialize the flow controls with two IconButtons
   */
  const initFlowControls = (): void => {
    try {
      const previewButtonConfig: IconButtonConfig = {
        icon: "preview_play",
        size: 44,
        iconSize: 28,
        behavior: "button",
        className: "flow-button flow-button--preview",
        ...(onPreviewClick
          ? {
              onClick: (_selected?: boolean) => {
                onPreviewClick();
              },
            }
          : {}),
      };
      previewButtonAPI = createIconButton(previewButtonConfig);

      // Create annotate button
      const annotateButtonConfig: IconButtonConfig = {
        icon: "interactions_annotate",
        size: 44,
        iconSize: 28,
        behavior: "button",
        className: "flow-button flow-button--annotate",
        ...(onAnnotateClick
          ? {
              onClick: (_selected?: boolean) => {
                onAnnotateClick();
              },
            }
          : {}),
      };
      annotateButtonAPI = createIconButton(annotateButtonConfig);

      // Append buttons to container
      container.appendChild(previewButtonAPI.getElement());
      container.appendChild(annotateButtonAPI.getElement());
      updateContainerVisibility();
    } catch (error) {
      console.error("Failed to create FlowControls component:", error);
      throw error;
    }
  };

  /**
   * Set preview button disabled state
   */
  const setPreviewDisabled = (disabled: boolean): void => {
    if (isDestroyed || !previewButtonAPI) return;
    previewButtonAPI.setDisabled(disabled);
  };

  /**
   * Set annotate button disabled state
   */
  const setAnnotateDisabled = (disabled: boolean): void => {
    if (isDestroyed || !annotateButtonAPI) return;
    annotateButtonAPI.setDisabled(disabled);
  };

  const setPreviewVisible = (visible: boolean): void => {
    if (isDestroyed) return;
    previewVisible = visible;
    if (previewButtonAPI) {
      previewButtonAPI
        .getElement()
        .style.setProperty("display", visible ? "" : "none");
    }
    updateContainerVisibility();
  };

  const setAnnotateVisible = (visible: boolean): void => {
    if (isDestroyed) return;
    annotateVisible = visible;
    if (annotateButtonAPI) {
      annotateButtonAPI
        .getElement()
        .style.setProperty("display", visible ? "" : "none");
    }
    updateContainerVisibility();
  };

  const setPreviewIcon = (icon: string): void => {
    if (isDestroyed || !previewButtonAPI) return;
    previewButtonAPI.setIcon(icon);
  };

  const setAnnotateIcon = (icon: string): void => {
    if (isDestroyed || !annotateButtonAPI) return;
    annotateButtonAPI.setIcon(icon);
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy button components
    previewButtonAPI?.destroy();
    annotateButtonAPI?.destroy();

    // Clear references
    previewButtonAPI = null;
    annotateButtonAPI = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initFlowControls();

  return {
    getElement: () => container,
    setPreviewDisabled,
    setAnnotateDisabled,
    setPreviewVisible,
    setAnnotateVisible,
    setPreviewIcon,
    setAnnotateIcon,
    destroy,
  };
};

/**
 * Creates FlowControls component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the flow controls
 * @returns FlowControls API
 */
export const createFlowControlsInContainer = (
  container: HTMLElement,
  config: FlowControlsConfig = {}
): FlowControlsAPI => {
  const flowControls = createFlowControls(config);
  container.appendChild(flowControls.getElement());
  return flowControls;
};
